import {
  UpdateUserAccount,
  UserAccount,
  UserAccountPaginatedResponse,
} from '@types';
import { Pagination, SearchBar } from '_components/atoms';
import { useAppSelector } from '_hooks';
import AdminService from '_services/AdminService';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const UserAccounts: React.FC = () => {
  const { accountDetails } = useAppSelector((state: any) => state.auth);
  const isAdmin = ['admin', 'super_admin'].includes(
    accountDetails?.roleKey || '',
  );

  const [users, setUsers] = useState<UserAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    record: UserAccount | null;
  }>({
    isOpen: false,
    record: null,
  });

  const [editForm, setEditForm] = useState<{
    givenName: string;
    familyName: string;
    roleKey: string;
  }>({
    givenName: '',
    familyName: '',
    roleKey: 'REGULAR',
  });
  const [updating, setUpdating] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const responseData = (await AdminService.getUserAccounts(
        pageNumber,
        pageSize,
        searchTerm,
      )) as unknown as UserAccountPaginatedResponse;

      if (responseData) {
        setUsers(responseData.users);
        setTotalRecords(responseData.pagination.totalRecords);
        setTotalPages(responseData.pagination.totalPages);
        setHasNext(responseData.pagination.hasNext);
        setHasPrevious(responseData.pagination.hasPrevious);
      }
    } catch (error) {
      console.error('Error fetching user accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, searchTerm]);

  const handleSearch = useCallback((term: string) => {
    if (term.length >= 3 || term.length === 0) {
      setSearchTerm(term);
      setPageNumber(1);
    }
  }, []);

  const handlePageChange = (newPage: number) => setPageNumber(newPage);
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  const getPhone = (u: UserAccount) => u.phone || u.phoneNumber || '';

  const openEditModal = (record: UserAccount) => {
    const currentRole = (record.roleKey || '').toLocaleLowerCase();
    setEditForm({
      givenName: record.givenName || '',
      familyName: record.familyName || '',
      roleKey: ['moderator', 'staff'].includes(currentRole)
        ? currentRole
        : 'member',
    });
    setEditModal({ isOpen: true, record });
    setUpdating(false);
  };

  const closeEditModal = () => {
    if (updating) return;
    setEditModal({ isOpen: false, record: null });
  };

  const onChangeRole = (role: 'member' | 'staff' | 'moderator') => {
    setEditForm(prev => ({ ...prev, roleKey: role.toLocaleLowerCase() }));
  };

  const ROLE_LABELS: Record<string, string> = {
    admin: 'Administrator',
    moderator: 'Manager',
    staff: 'Staff',
    member: 'Customer',
  };

  const roleBadgeClass = (roleKey: string) => {
    switch ((roleKey || '').toLowerCase()) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'moderator':
        return 'bg-purple-100 text-purple-800';
      case 'staff':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const roleLabel = (roleKey: string) =>
    ROLE_LABELS[(roleKey || '').toLowerCase()] || (roleKey || '').toUpperCase();

  const canSubmit = useMemo(() => {
    if (!editModal.record) return false;
    if (!editForm.givenName.trim() || !editForm.familyName.trim()) return false;
    return true;
  }, [editForm.givenName, editForm.familyName, editModal.record]);

  const handleUpdate = async () => {
    if (!editModal.record || !canSubmit) return;
    try {
      setUpdating(true);
      const updated: UpdateUserAccount = {
        id: editModal.record.id,
        email: editModal.record.email,
        givenName: editForm.givenName.trim(),
        familyName: editForm.familyName.trim(),
        roleKey: editForm.roleKey.toLocaleLowerCase(),
        isActive: editModal.record.isActive,
      };
      await AdminService.updateUserAccount(updated);
      await fetchUsers();
      setEditModal({ isOpen: false, record: null });
    } catch (error) {
      console.error('Error updating user account:', error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="mt-1 text-sm text-gray-600">
            Look up customer accounts, passes, and waivers
            {isAdmin ? ', and manage roles' : ''}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Records: <span className="font-semibold">{totalRecords}</span>
          </div>
        </div>
      </div>

      <SearchBar
        placeholder="Search by name, email, or phone..."
        onSearch={handleSearch}
        debounceMs={1000}
      />

      <Pagination
        pageNumber={pageNumber}
        totalPages={totalPages}
        pageSize={pageSize}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onReload={fetchUsers}
      />

      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No users found
                  </td>
                </tr>
              ) : (
                users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-sm">
                          {`${(u.givenName || '?')[0] || ''}${(u.familyName || '?')[0] || ''}`}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-semibold text-gray-900">
                            {u.givenName} {u.familyName}
                          </div>
                          <div className="text-xs text-gray-500">
                            Joined{' '}
                            {HelperMethods.formatDateTimeLocal(u.createdAt)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{u.email}</div>
                      <div className="text-xs text-gray-500">{getPhone(u)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${roleBadgeClass(
                          u.roleKey || '',
                        )}`}
                      >
                        {roleLabel(u.roleKey || '')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          u.isActive
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        <span
                          className={`mr-1 inline-block h-2 w-2 rounded-full ${
                            u.isActive ? 'bg-emerald-500' : 'bg-gray-500'
                          }`}
                        />
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`/admin/user-accounts/${u.id}`}
                          className="inline-flex items-center px-3 py-1.5 border border-blue-200 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-50 hover:border-blue-300 hover:text-blue-800 transition-colors"
                        >
                          View
                        </Link>
                        {isAdmin && (
                          <button
                            onClick={() => openEditModal(u)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-200 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden space-y-4">
        {users.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            No users found
          </div>
        ) : (
          users.map(u => (
            <div
              key={u.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-sm">
                    {`${(u.givenName || '?')[0] || ''}${(u.familyName || '?')[0] || ''}`}
                  </div>
                  <div className="ml-3">
                    <h3 className="text-base font-semibold text-gray-900">
                      {u.givenName} {u.familyName}
                    </h3>
                    <div className="text-xs text-gray-500">
                      Joined {HelperMethods.formatDateTimeLocal(u.createdAt)}
                    </div>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${roleBadgeClass(
                    u.roleKey || '',
                  )}`}
                >
                  {roleLabel(u.roleKey || '')}
                </span>
              </div>
              <div className="space-y-1.5">
                <div className="text-sm text-gray-900">{u.email}</div>
                <div className="text-sm text-gray-500">{getPhone(u)}</div>
                <div>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      u.isActive
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    <span
                      className={`mr-1 inline-block h-2 w-2 rounded-full ${
                        u.isActive ? 'bg-emerald-500' : 'bg-gray-500'
                      }`}
                    />
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Link
                  to={`/admin/user-accounts/${u.id}`}
                  className="inline-flex items-center px-3 py-2 border border-blue-200 text-blue-700 text-sm rounded-md hover:bg-blue-50 hover:border-blue-300 hover:text-blue-800 transition-colors"
                >
                  View
                </Link>
                {isAdmin && (
                  <button
                    onClick={() => openEditModal(u)}
                    className="inline-flex items-center px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {editModal.isOpen && editModal.record && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
              <button
                onClick={closeEditModal}
                disabled={updating}
                className={`text-gray-400 hover:text-gray-600 transition-colors ${
                  updating ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Given Name
                </label>
                <input
                  type="text"
                  value={editForm.givenName}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      givenName: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={updating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Family Name
                </label>
                <input
                  type="text"
                  value={editForm.familyName}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      familyName: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={updating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => onChangeRole('member')}
                    disabled={updating}
                    className={`px-3 py-2 rounded-md text-sm border ${
                      editForm.roleKey === 'member'
                        ? 'bg-blue-50 border-blue-300 text-blue-800'
                        : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >
                    Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => onChangeRole('staff')}
                    disabled={updating}
                    className={`px-3 py-2 rounded-md text-sm border ${
                      editForm.roleKey === 'staff'
                        ? 'bg-teal-50 border-teal-300 text-teal-800'
                        : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >
                    Staff
                  </button>
                  <button
                    type="button"
                    onClick={() => onChangeRole('moderator')}
                    disabled={updating}
                    className={`px-3 py-2 rounded-md text-sm border ${
                      editForm.roleKey === 'moderator'
                        ? 'bg-purple-50 border-purple-300 text-purple-800'
                        : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >
                    Manager
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Staff can view tickets but cannot cancel or delete them.
                  Managers can cancel tickets. Only Administrators can delete
                  tickets (assign that role directly in the database).
                </p>
              </div>
              <div className="text-sm text-gray-500">
                Email: {editModal.record.email}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeEditModal}
                disabled={updating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={!canSubmit || updating}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccounts;
